import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Plus, Send, X, HelpCircle, MessageCircle,
  CheckCircle2, ArrowUp, Loader2
} from 'lucide-react';
import { API } from './shared';
import { parseUtc } from '../../lib/calendarDate';

const QuestionsTab = ({ communityId, isMember, isAdmin, currentUserId, questions, loading, filter, setFilter, onRefresh, navigate }) => {
  const [showAsk, setShowAsk] = useState(false);
  const [selectedQ, setSelectedQ] = useState(null);

  const statusColors = { pending: 'bg-yellow-100 text-yellow-700', answered: 'bg-blue-100 text-blue-700', resolved: 'bg-green-100 text-green-700' };
  const statusLabels = { pending: 'En attente', answered: 'Repondu', resolved: 'Resolu' };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl shadow-lg p-4 flex items-center gap-3 flex-wrap">
        {['all', 'pending', 'answered', 'resolved'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === f ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`filter-${f}`}>
            {f === 'all' ? 'Toutes' : statusLabels[f]}
          </button>
        ))}
        {isMember && <Button size="sm" className="ml-auto bg-gradient-to-r from-purple-500 to-pink-500 text-white" onClick={() => setShowAsk(true)} data-testid="ask-question-btn"><Plus className="h-4 w-4 mr-1" />Poser une question</Button>}
      </div>

      {loading ? <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-purple-500" /></div> : questions.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center"><HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune question</h3><p className="text-gray-500">{isMember ? "Posez la premiere question !" : "Les questions apparaitront ici."}</p></div>
      ) : (
        <div className="space-y-3">
          {questions.map(q => (
            <div key={q.question_id} onClick={() => setSelectedQ(q.question_id)} className="bg-white rounded-2xl shadow-lg p-5 hover:shadow-xl transition-shadow cursor-pointer" data-testid={`question-${q.question_id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="h-9 w-9 flex-shrink-0"><AvatarImage src={q.author_avatar} /><AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs">{q.author_name?.[0]}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-900 text-sm">{q.title}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[q.status] || statusColors.pending}`}>{statusLabels[q.status] || q.status}</span>
                  </div>
                  {q.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{q.description}</p>}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span>{q.author_name}</span>
                    <span>{parseUtc(q.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                    <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{q.answer_count || 0} reponse{(q.answer_count || 0) > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAsk && <AskQuestionModal communityId={communityId} onClose={() => setShowAsk(false)} onCreated={() => { setShowAsk(false); onRefresh(); }} />}
      {selectedQ && <QuestionDetailModal questionId={selectedQ} communityId={communityId} isMember={isMember} isAdmin={isAdmin} currentUserId={currentUserId} onClose={() => setSelectedQ(null)} onRefresh={onRefresh} navigate={navigate} />}
    </div>
  );
};


const AskQuestionModal = ({ communityId, onClose, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/communities/${communityId}/questions`, { title: title.trim(), description: description.trim() }, { withCredentials: true });
      onCreated();
    } catch (error) { console.error(error); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="ask-question-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
        <div className="p-6 border-b flex items-center justify-between"><h2 className="text-xl font-bold text-gray-900">Poser une question</h2><button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button></div>
        <div className="p-6 space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Question *</label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Votre question..." data-testid="question-title-input" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Details (optionnel)</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ajoutez plus de contexte..." className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm" rows={4} data-testid="question-desc-input" /></div>
        </div>
        <div className="p-6 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving || !title.trim()} className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white" data-testid="submit-question-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}Publier</Button>
        </div>
      </div>
    </div>
  );
};


const QuestionDetailModal = ({ questionId, communityId, isMember, isAdmin, currentUserId, onClose, onRefresh, navigate }) => {
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newAnswer, setNewAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadDetail(); }, [questionId]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/communities/questions/${questionId}`, { withCredentials: true });
      setQuestion(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleAnswer = async () => {
    if (!newAnswer.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/communities/questions/${questionId}/answers`, { content: newAnswer.trim() }, { withCredentials: true });
      setNewAnswer('');
      loadDetail();
      onRefresh();
    } catch (error) { console.error(error); }
    finally { setSubmitting(false); }
  };

  const handleVote = async (answerId) => {
    try {
      await axios.post(`${API}/communities/answers/${answerId}/vote`, {}, { withCredentials: true });
      loadDetail();
    } catch (error) { console.error(error); }
  };

  const handleResolve = async () => {
    try {
      await axios.post(`${API}/communities/questions/${questionId}/resolve`, {}, { withCredentials: true });
      loadDetail();
      onRefresh();
    } catch (error) { console.error(error); }
  };

  const statusColors = { pending: 'bg-yellow-100 text-yellow-700', answered: 'bg-blue-100 text-blue-700', resolved: 'bg-green-100 text-green-700' };
  const statusLabels = { pending: 'En attente', answered: 'Repondu', resolved: 'Resolu' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="question-detail-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">Question</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
        </div>

        {loading ? <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-purple-500" /></div> : question && (
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-10 w-10 cursor-pointer" onClick={() => { onClose(); navigate(`/app/profile/${question.user_id}`); }}><AvatarImage src={question.author_avatar} /><AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">{question.author_name?.[0]}</AvatarFallback></Avatar>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{question.author_name}</p>
                  <p className="text-xs text-gray-500">{parseUtc(question.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
                <span className={`ml-auto px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[question.status]}`}>{statusLabels[question.status]}</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900">{question.title}</h3>
              {question.description && <p className="text-gray-600 mt-2 leading-relaxed text-sm">{question.description}</p>}
              {question.status !== 'resolved' && (currentUserId === question.user_id || isAdmin) && (
                <Button size="sm" variant="outline" onClick={handleResolve} className="mt-3 text-green-600 border-green-200 hover:bg-green-50" data-testid="resolve-question-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Marquer comme resolu</Button>
              )}
            </div>

            <div className="p-6">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">{question.answers?.length || 0} Reponse{(question.answers?.length || 0) > 1 ? 's' : ''}</h4>
              {question.answers?.length > 0 ? (
                <div className="space-y-4">
                  {question.answers.map(answer => (
                    <div key={answer.answer_id} className="flex gap-3 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors" data-testid={`answer-${answer.answer_id}`}>
                      <div className="flex flex-col items-center gap-1 flex-shrink-0">
                        <button onClick={() => handleVote(answer.answer_id)} className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors" data-testid={`vote-${answer.answer_id}`}><ArrowUp className="h-5 w-5" /></button>
                        <span className="text-sm font-bold text-gray-700">{answer.vote_count || 0}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar className="h-7 w-7 cursor-pointer" onClick={() => { onClose(); navigate(`/app/profile/${answer.user_id}`); }}><AvatarImage src={answer.author_avatar} /><AvatarFallback className="bg-gradient-to-br from-cyan-500 to-purple-500 text-white text-xs">{answer.author_name?.[0]}</AvatarFallback></Avatar>
                          <span className="text-sm font-medium text-gray-900">{answer.author_name}</span>
                          <span className="text-xs text-gray-400">{parseUtc(answer.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                          {answer.is_best && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Meilleure reponse</span>}
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{answer.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-400 text-sm text-center py-4">Aucune reponse pour le moment. Soyez le premier !</p>}

              {isMember && (
                <div className="mt-6 pt-4 border-t">
                  <textarea value={newAnswer} onChange={(e) => setNewAnswer(e.target.value)} placeholder="Votre reponse..." className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm" rows={3} data-testid="answer-input" />
                  <div className="flex justify-end mt-2">
                    <Button onClick={handleAnswer} disabled={submitting || !newAnswer.trim()} size="sm" className="bg-gradient-to-r from-purple-500 to-pink-500 text-white" data-testid="submit-answer-btn">{submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}Repondre</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionsTab;
